import type { OnDestroy } from '@angular/core';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    HostListener,
    inject,
    Injector,
    type OnInit,
    runInInjectionContext,
    signal,
    ViewChild,
    type WritableSignal,
} from '@angular/core';
import type { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import type { CdkDragMove } from '@angular/cdk/drag-drop';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { TranslatePipe } from '@ngx-translate/core';

import { Widgets } from '@common/enums';
import { type Bounds, type Target, WebWidgetBoundaryService } from '@web/core/services/web-widget-boundary.service';

import type { ExtendedPositionLegacy } from '@sites/entity-core';
import type {
    ButtonZeroForm,
    FigureZeroForm,
    FormWidgetZeroForm,
    ImageZeroForm,
    LoginEduZeroForm,
    PaymentFormWidgetZeroForm,
    SocialZeroForm,
    TextZeroForm,
    VideoZeroForm,
} from '@sites/entity-signal-form';

import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';
import { ContentFacade } from '@web/store/website/content/content.facade';

import { BaseSignalWidgetComponent } from '../base-signal-widget.abstract';
import { ViewResolverComponent } from '../../../resolvers/view-resolver.component';
import { ZeroWidgetControlsComponent } from '../../controls/zero-widget-controls/zero-widget-controls.component';
import { DragHandleComponent } from '../../drag-handle/drag-handle.component';
import { VerticalResizeHandlerDirective } from '../../../directives/vertical-resize-handler.directive';
import { ResizeCornerHandleDirective } from '../../../directives/corner-resize-handler.directive';
import { HorizontalResizeHandlerDirective } from '../../../directives/horizontal-resize-handler.directive';
import { RotationHandlerDirective } from '../../../directives/rotation-handler.directive';

type DimKey = 'x' | 'y' | 'width' | 'height' | 'rotate' | 'zIndex';
type PosFlagsKey = 'isMobilePositionChanged' | 'isTabletPositionChanged';

/**
 * Union of every zero-variant signal form this host can drive. All members
 * expose the same layout surface (`position` as `FieldTree<ExtendedPositionLegacy>`,
 * `setPosition`, `hideOnDesktop`/`hideOnTablet`/`hideOnMobile`), which is the
 * only part of the form this component touches.
 */
type ZeroWidgetSignalForm =
    | VideoZeroForm
    | ButtonZeroForm
    | FigureZeroForm
    | FormWidgetZeroForm
    | ImageZeroForm
    | LoginEduZeroForm
    | PaymentFormWidgetZeroForm
    | SocialZeroForm
    | TextZeroForm;

/** See {@link ZeroSignalWidgetComponent.canChangeWidgetHeight}. */
const CONTENT_DRIVEN_HEIGHT: string[] = [
    Widgets.BUTTON,
    Widgets.FORM,
    Widgets.PAYMENT_FORM,
    Widgets.SOCIAL_NETWORKS,
    Widgets.TEXT,
    Widgets.LOGIN_EDU,
];
const CONTENT_DRIVEN_WIDTH: string[] = [Widgets.SOCIAL_NETWORKS];

@Component({
    selector: 'web-zero-signal-widget',
    templateUrl: './zero-signal-widget.component.html',
    styleUrls: ['./zero-signal-widget.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        TranslatePipe,
        TooltipModule,
        CdkDrag,
        CdkDragHandle,
        ViewResolverComponent,
        ZeroWidgetControlsComponent,
        DragHandleComponent,
        VerticalResizeHandlerDirective,
        ResizeCornerHandleDirective,
        HorizontalResizeHandlerDirective,
        RotationHandlerDirective,
    ],
})
export class ZeroSignalWidgetComponent extends BaseSignalWidgetComponent implements OnInit, OnDestroy {
    private readonly widgetBoundaryService = inject(WebWidgetBoundaryService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly signalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly injector = inject(Injector);
    private readonly contentFacade = inject(ContentFacade);

    protected readonly Widgets = Widgets;

    // --- Boundary guide signals (mirrored from WebWidgetBoundaryService) ---

    public readonly showHorizontalCenterBoundary = computed(
        () => this.widgetBoundaryService.widgetHorizontalCenterId() === this.widget().id
    );
    public readonly showVerticalCenterBoundary = computed(
        () => this.widgetBoundaryService.widgetVerticalCenterId() === this.widget().id
    );
    public readonly showTopBoundary = computed(() => this.widgetBoundaryService.widgetTopId() === this.widget().id);
    public readonly showBottomBoundary = computed(
        () => this.widgetBoundaryService.widgetBottomId() === this.widget().id
    );
    public readonly showLeftBoundary = computed(() => this.widgetBoundaryService.widgetLeftId() === this.widget().id);
    public readonly showRightBoundary = computed(() => this.widgetBoundaryService.widgetRightId() === this.widget().id);

    @ViewChild('rotateTooltipRef') private rotateTooltipRef: TooltipDirective | undefined;

    // --- UI state (writable signals) ---

    public readonly isDragging = signal(false);
    public readonly isRotating = signal(false);
    public readonly isKeyboardMoving = signal(false);
    public readonly canDrag = signal(true);

    /**
     * True for widgets edited inline (text) — entering the inline editor on
     * double-click disables direct drag (a drag-handle is offered instead).
     */
    private readonly isInlineEditable = computed(() => this.form()?.value().name === Widgets.TEXT);

    /**
     * Per-widget resize capabilities. Button, form, payment-form, social, text
     * and login-edu heights are content-driven: the views DOM-sync the rendered
     * height into the grid, so manual height resize is disabled. Social
     * measures its WIDTH from the DOM as well (a row of icons), so both axes are
     * off there — a width drag would be overwritten by the next measurement.
     * Text and login-edu are width-resizable (their height re-syncs from the DOM
     * when the width changes).
     */
    public readonly canChangeWidgetWidth = computed<boolean>(
        () => !CONTENT_DRIVEN_WIDTH.includes(this.widget().name as string)
    );
    public readonly canChangeWidgetHeight = computed<boolean>(
        () => !CONTENT_DRIVEN_HEIGHT.includes(this.widget().name as string)
    );

    private keyboardMoveTimeout: ReturnType<typeof setTimeout> | null = null;

    /**
     * Pre-drag x/y baseline (active-device fields). `cdkDragMoved` reports the
     * **cumulative** distance from drag start, so the next position must be
     * computed against the values at gesture start — during the gesture the
     * form already holds the per-frame writes (batched by the open
     * transaction), so reading it live would compound the distance.
     */
    private dragStartPoint: { x: number; y: number } | null = null;

    public readonly widgetBoundaryWidth = signal(0);
    public readonly widgetBoundaryHeight = signal(0);

    /** The signal form for this widget — populated in `ngOnInit`. */
    public readonly form: WritableSignal<ZeroWidgetSignalForm | null> = signal(null);

    public ngOnInit(): void {
        const f = this.getOrCreateForm();
        if (!f) {
            console.warn(
                `[ZeroSignalWidgetComponent] no signal-form service registered for ${this.widget().name}; ` +
                    `add a branch to getOrCreateForm() or extract a registry.`
            );
            return;
        }
        this.form.set(f);

        // `effect()` requires an injection context. `ngOnInit` runs outside
        // one (post-constructor), so route through the component's injector
        // explicitly. The effects auto-clean up when the component is
        // destroyed because they're registered against this injector.
        runInInjectionContext(this.injector, () => {
            // Initial boundary calculation + reactive recompute whenever the
            // position FieldTree's value or the device mode changes. Replaces
            // the `position.valueChanges.subscribe(...)` rxjs path on the
            // FormGroup host.
            effect(() => {
                const form = this.form();
                if (!form) return;
                // Read width/height/rotate through the active-mode-aware computeds
                // so the effect re-tracks when device-specific fields change too.
                this.widthValue();
                this.heightValue();
                this.rotateValue();
                this.updateBoundaries();
            });
        });
    }

    private getOrCreateForm(): ZeroWidgetSignalForm | null {
        // The registry dispatches on the widget's `name`; the cast to the
        // zero-variant union is safe because this host is only mounted for
        // widgets inside zero blocks (the form factories pick the zero
        // variant from the entity's `type: 'zero'` tag).
        return this.signalFormService.getOrCreateScoped<ZeroWidgetSignalForm>(this.widget().id, this.destroyRef);
    }

    // --- Form access helpers ---

    /**
     * Non-null unwrap of {@link form} for the many internal readers. The `!`
     * is safe everywhere it's used: those paths only run after `ngOnInit`
     * populated the signal (computeds/effects guard on `form()` themselves,
     * and gesture handlers can't fire before the widget rendered).
     */
    private get zeroForm(): ZeroWidgetSignalForm {
        return this.form()!;
    }

    // --- Keyboard moves ---

    @HostListener('document:keydown', ['$event'])
    public onKeydown(event: KeyboardEvent): void {
        if (!this.isWidgetSelected() || !this.canDrag()) return;

        if ((event.target as HTMLElement)?.closest?.('.settings-panel')) return;

        const step = event.shiftKey ? 10 : 1;

        switch (event.key) {
            case 'ArrowUp':
                event.preventDefault();
                this.setY(this.yValue() - step);
                break;
            case 'ArrowDown':
                event.preventDefault();
                this.setY(this.yValue() + step);
                break;
            case 'ArrowLeft':
                event.preventDefault();
                this.setX(this.xValue() - step);
                break;
            case 'ArrowRight':
                event.preventDefault();
                this.setX(this.xValue() + step);
                break;
            default:
                return;
        }

        this.updateBoundaries();
        this.showKeyboardGuidelines();
        this.checkAlignmentGuides();
    }

    private showKeyboardGuidelines(): void {
        this.isKeyboardMoving.set(true);

        if (this.keyboardMoveTimeout) clearTimeout(this.keyboardMoveTimeout);
        this.keyboardMoveTimeout = setTimeout(() => {
            this.hideKeyboardGuidelines();
        }, 3000);
    }

    private hideKeyboardGuidelines(): void {
        this.isKeyboardMoving.set(false);
        if (this.keyboardMoveTimeout) {
            clearTimeout(this.keyboardMoveTimeout);
            this.keyboardMoveTimeout = null;
        }

        this.clearAllGuides();
        this.candidateTargets = [];
        this.columnBounds = null;
    }

    private checkAlignmentGuides(): void {
        if (!this.candidateTargets.length) {
            this.collectCandidateTargets();
            this.loadColumnBounds();
        }

        const self = this.getSelfBounds();
        let snapX: number | null = null;
        let snapY: number | null = null;
        let guidesShown = false;

        for (const candidate of this.candidateTargets) {
            const centers = this.getBounds(candidate.element);
            const snap = this.computeWidgetSnap(self, centers);
            if (!snap) continue;

            this.showGuides({
                horizontalCenter: snap.showHorizontal,
                verticalCenter: snap.showVertical,
                top: snap.showTop,
                bottom: snap.showBottom,
                left: snap.showLeft,
                right: snap.showRight,
                id: candidate.element.id,
            });
            guidesShown = true;

            if (snap.snapX !== null) {
                snapX = snap.snapX;
                this.setX(snapX);
            }
            if (snap.snapY !== null) {
                snapY = snap.snapY;
                this.setY(snapY);
            }

            break;
        }

        if (!guidesShown) {
            this.clearAllGuides();
            return;
        }

        this.applyColumnSnapping(snapX, snapY);
    }

    public copyWidget(): void {
        this.form()?.copy({ index: this.index() });
    }

    public removeWidget(): void {
        this.form()?.remove({ index: this.index() });
    }

    // --- Boundary geometry ---

    private updateBoundaries(): void {
        this.widgetBoundaryWidth.set(this.calcWidgetBoundaryWidth());
        this.widgetBoundaryHeight.set(this.calcWidgetBoundaryHeight());
        this.widgetBoundaryService.updateTargetCandidate(this.widget().id, this.getSelfBounds());
    }

    public calcWidgetBoundaryHeight(): number {
        return (
            Math.abs(this.widthValue() * Math.sin(this.angle())) + Math.abs(this.heightValue() * Math.cos(this.angle()))
        );
    }
    public calcWidgetBoundaryWidth(): number {
        return (
            Math.abs(this.widthValue() * Math.cos(this.angle())) + Math.abs(this.heightValue() * Math.sin(this.angle()))
        );
    }

    private activeMode(): 'mobile' | 'tablet' | 'desktop' {
        if (this.isMobile()) return 'mobile';
        if (this.isTablet()) return 'tablet';
        return 'desktop';
    }

    private cap(s: string): string {
        return s.charAt(0).toUpperCase() + s.slice(1);
    }

    private fieldName<K extends DimKey>(key: K): K | `tablet${Capitalize<K>}` | `mobile${Capitalize<K>}` {
        const mode = this.activeMode();
        if (mode === 'desktop') return key;
        return (mode + this.cap(key)) as any;
    }

    private flagKeyForMode(): PosFlagsKey | null {
        if (this.isMobile()) return 'isMobilePositionChanged';
        if (this.isTablet()) return 'isTabletPositionChanged';
        return null;
    }

    /**
     * Writes a position field straight to the signal form's FieldTree leaf.
     * Per-frame gesture writes don't ping the store: the gesture handlers
     * wrap the whole gesture in a form transaction, which suppresses the
     * form's `onUpdate` (store dispatch + history) until commit.
     */
    private setVal<K extends DimKey>(key: K, value: number): void {
        const fname = this.fieldName(key);

        if (key === 'x' || key === 'y') {
            const flag = this.flagKeyForMode();
            if (flag) {
                (this.zeroForm.position as any)[flag]().value.set(true);
            }
        }

        (this.zeroForm.position as any)[fname]().value.set(value);
    }

    private getVal<K extends DimKey>(key: K, fallback = 0): number {
        const fname = this.fieldName(key);
        const pos = this.zeroForm.position().value() as any;
        return (pos?.[fname] ?? fallback) as number;
    }

    public setX(v: number): void {
        this.setVal('x', v);
    }
    public setY(v: number): void {
        this.setVal('y', v);
    }
    public setWidth(v: number): void {
        this.setVal('width', v);
    }
    public setHeight(v: number): void {
        this.setVal('height', v);
    }
    public setRotate(v: number): void {
        this.setVal('rotate', v);
    }

    public readonly widthValue = computed(() => this.getVal('width', 100));
    public readonly heightValue = computed(() => this.getVal('height'));
    public readonly xValue = computed(() => this.getVal('x'));
    public readonly yValue = computed(() => this.getVal('y'));
    public readonly rotateValue = computed(() => this.getVal('rotate'));

    /**
     * Whether resize should preserve the widget's aspect ratio. Capability-driven:
     * any zero form that exposes a `keepProportions` accessor opts in (today only
     * figure does); every other form in the union resizes freely. Drives both the
     * corner handles' directive input and the proportional branch in the edge
     * handlers below.
     */
    public readonly keepProportions = computed<boolean>(() => {
        const form = this.form();
        if (!form || !('keepProportions' in form)) {
            return false;
        }
        return (form as FigureZeroForm).keepProportions().value();
    });

    /**
     * Z-index. Hidden widgets retreat far behind so they don't intercept
     * pointer events. (When `showWidget` is false, the host returns -9999
     * to match the FormGroup host's behaviour.)
     */
    public readonly zIndexValue = computed(() => {
        if (!this.showWidget()) return -9999;
        return this.getVal('zIndex');
    });

    private gestureActive = false;

    private beginGesture(): void {
        this.gestureActive = true;
        this.contentFacade.setAutoSavePaused(true);
        this.zeroForm.beginTransaction();
    }

    private commitGesture(): void {
        const form = this.form();
        if (form?.inTransaction()) {
            form.commitTransaction();
        }
        this.gestureActive = false;
        this.contentFacade.setAutoSavePaused(false);
    }

    // --- Alignment / snapping ---

    private overWidgetBoundaryId: string | null = null;
    private readonly DEFAULT_TOLERANCE = 6;
    private readonly EDGE_PAD = 2;
    private readonly CENTER_PAD = 1.5;
    private readonly RIGHT_PAD = 1;

    private candidateTargets: Target[] = [];
    private columnBounds: { width: number; height: number; horizontalCenter: number; verticalCenter: number } | null =
        null;

    private within(a: number, b: number, tol = this.DEFAULT_TOLERANCE): boolean {
        return Math.abs(a - b) <= tol;
    }

    private clearAllGuides(): void {
        this.showGuides({
            horizontalCenter: false,
            verticalCenter: false,
            top: false,
            bottom: false,
            left: false,
            right: false,
            id: null,
        });
        this.showColumnGuides({ horizontalCenter: false, verticalCenter: false });
    }

    private loadColumnBounds(): void {
        const parentColumn = document.getElementById(this.widget().columnId);
        if (parentColumn) {
            this.columnBounds = {
                width: parentColumn.offsetWidth,
                height: parentColumn.offsetHeight,
                horizontalCenter: parentColumn.offsetWidth / 2,
                verticalCenter: parentColumn.offsetHeight / 2,
            };
        }
    }

    private computeWidgetSnap(
        self: Bounds,
        centers: Bounds
    ): {
        snapX: number | null;
        snapY: number | null;
        showHorizontal: boolean;
        showVertical: boolean;
        showTop: boolean;
        showBottom: boolean;
        showLeft: boolean;
        showRight: boolean;
    } | null {
        const withinHorizontalLeft = this.within(self.left, centers.horizontalCenter);
        const withinHorizontalCenter = this.within(self.horizontalCenter, centers.horizontalCenter);
        const withinHorizontalRight = this.within(self.right, centers.horizontalCenter);

        const withinVerticalTop = this.within(self.top, centers.verticalCenter);
        const withinVerticalCenter = this.within(self.verticalCenter, centers.verticalCenter);
        const withinVerticalBottom = this.within(self.bottom, centers.verticalCenter);

        const withinTopTop = this.within(self.top, centers.top);
        const withinTopCenter = this.within(self.verticalCenter, centers.top);
        const withinTopBottom = this.within(self.bottom, centers.top);

        const withinBottomTop = this.within(self.top, centers.bottom);
        const withinBottomCenter = this.within(self.verticalCenter, centers.bottom);
        const withinBottomBottom = this.within(self.bottom, centers.bottom);

        const withinLeftLeft = this.within(self.left, centers.left);
        const withinLeftCenter = this.within(self.horizontalCenter, centers.left);
        const withinLeftRight = this.within(self.right, centers.left);

        const withinRightLeft = this.within(self.left, centers.right);
        const withinRightCenter = this.within(self.horizontalCenter, centers.right);
        const withinRightRight = this.within(self.right, centers.right);

        const showHorizontal = withinHorizontalLeft || withinHorizontalCenter || withinHorizontalRight;
        const showVertical = withinVerticalTop || withinVerticalCenter || withinVerticalBottom;
        const showTop = withinTopTop || withinTopCenter || withinTopBottom;
        const showBottom = withinBottomTop || withinBottomCenter || withinBottomBottom;
        const showLeft = withinLeftLeft || withinLeftCenter || withinLeftRight;
        const showRight = withinRightLeft || withinRightCenter || withinRightRight;

        if (!showHorizontal && !showVertical && !showTop && !showBottom && !showLeft && !showRight) {
            return null;
        }

        let snapX: number | null = null;
        if (withinHorizontalCenter) {
            snapX = centers.horizontalCenter + this.CENTER_PAD - self.width / 2;
        } else if (withinHorizontalLeft) {
            snapX = centers.horizontalCenter + this.EDGE_PAD;
        } else if (withinHorizontalRight) {
            snapX = centers.horizontalCenter + this.RIGHT_PAD - self.width;
        } else if (withinLeftCenter) {
            snapX = centers.left + this.EDGE_PAD - self.width / 2;
        } else if (withinLeftLeft) {
            snapX = centers.left + this.EDGE_PAD;
        } else if (withinLeftRight) {
            snapX = centers.left + this.RIGHT_PAD - self.width;
        } else if (withinRightCenter) {
            snapX = centers.right - this.EDGE_PAD - self.width / 2;
        } else if (withinRightLeft) {
            snapX = centers.right - this.RIGHT_PAD;
        } else if (withinRightRight) {
            snapX = centers.right - this.EDGE_PAD - self.width;
        }

        let snapY: number | null = null;
        if (withinVerticalCenter) {
            snapY = centers.verticalCenter + this.CENTER_PAD - self.height / 2;
        } else if (withinVerticalTop) {
            snapY = centers.verticalCenter + this.EDGE_PAD;
        } else if (withinVerticalBottom) {
            snapY = centers.verticalCenter + this.RIGHT_PAD - self.height;
        } else if (withinTopCenter) {
            snapY = centers.top + this.CENTER_PAD - self.height / 2;
        } else if (withinTopTop) {
            snapY = centers.top + this.EDGE_PAD;
        } else if (withinTopBottom) {
            snapY = centers.top + this.RIGHT_PAD - self.height;
        } else if (withinBottomCenter) {
            snapY = centers.bottom - this.EDGE_PAD - self.height / 2;
        } else if (withinBottomTop) {
            snapY = centers.bottom - this.RIGHT_PAD;
        } else if (withinBottomBottom) {
            snapY = centers.bottom - this.EDGE_PAD - self.height;
        }

        return { snapX, snapY, showHorizontal, showVertical, showTop, showBottom, showLeft, showRight };
    }

    private applyColumnSnapping(snapX: number | null, snapY: number | null): void {
        if (!this.columnBounds) return;

        const updatedSelf = this.getSelfBounds();
        let showColH = false;
        let showColV = false;

        if (snapX === null) {
            if (this.within(updatedSelf.horizontalCenter, this.columnBounds.horizontalCenter)) {
                this.setX(this.columnBounds.horizontalCenter - updatedSelf.width / 2);
                showColH = true;
            } else if (this.within(updatedSelf.left, this.columnBounds.horizontalCenter)) {
                this.setX(this.columnBounds.horizontalCenter);
                showColH = true;
            } else if (this.within(updatedSelf.right, this.columnBounds.horizontalCenter)) {
                this.setX(this.columnBounds.horizontalCenter - updatedSelf.width);
                showColH = true;
            }
        }

        if (snapY === null) {
            if (this.within(updatedSelf.verticalCenter, this.columnBounds.verticalCenter)) {
                this.setY(this.columnBounds.verticalCenter - updatedSelf.height / 2);
                showColV = true;
            } else if (this.within(updatedSelf.top, this.columnBounds.verticalCenter)) {
                this.setY(this.columnBounds.verticalCenter);
                showColV = true;
            } else if (this.within(updatedSelf.bottom, this.columnBounds.verticalCenter)) {
                this.setY(this.columnBounds.verticalCenter - updatedSelf.height);
                showColV = true;
            }
        }

        this.showColumnGuides({ horizontalCenter: showColH, verticalCenter: showColV });
    }

    private collectCandidateTargets(): void {
        this.candidateTargets = this.widgetBoundaryService
            .getTargetCandidatesArray()
            .filter((candidate) => candidate.id !== this.widget().id);
    }

    private intersectionArea(self: Bounds, target: Bounds): number {
        const w = Math.max(0, Math.min(self.right, target.right) - Math.max(self.left, target.left));
        const h = Math.max(0, Math.min(self.bottom, target.bottom) - Math.max(self.top, target.top));
        return w * h;
    }

    private findHitUnderDragged({ fallback }: { fallback: { cx: number; cy: number } }): HTMLElement | null {
        const selfBounds = this.getSelfBoundsWithMargin();
        const gapPx = 24; // how far away to search for targets around the dragging element

        const near: Target[] = [];

        for (const t of this.candidateTargets) {
            const targetBounds = t.bounds;
            if (
                targetBounds.right < selfBounds.left - gapPx ||
                targetBounds.left > selfBounds.right + gapPx ||
                targetBounds.bottom < selfBounds.top - gapPx ||
                targetBounds.top > selfBounds.bottom + gapPx
            ) {
                continue;
            }
            near.push(t);
        }

        if (near.length === 0) return null;

        let best: { el: HTMLElement; area: number } | null = null;

        for (const t of near) {
            const targetBounds = t.bounds;
            const area = this.intersectionArea(selfBounds, targetBounds);
            if (area <= 0) continue;

            if (!best || area > best.area) best = { el: t.element, area };
        }

        if (best) return best.el;

        const underCenter = document.elementFromPoint(fallback.cx, fallback.cy) as HTMLElement | null;
        return (underCenter?.closest('[data-boundary-target="true"]') as HTMLElement | null) ?? null;
    }

    private getBounds(hit: HTMLElement): Bounds {
        const left = hit.offsetLeft;
        const top = hit.offsetTop;
        const width = hit.offsetWidth;
        const height = hit.offsetHeight;

        return {
            horizontalCenter: left + width / 2,
            verticalCenter: top + height / 2,
            left,
            top,
            right: left + width,
            bottom: top + height,
            width,
            height,
        };
    }

    private getSelfBounds(): Bounds {
        const leftX = this.leftXValue() ?? 0;
        const topY = this.topYValue() ?? 0;
        const width = this.widgetBoundaryWidth();
        const height = this.widgetBoundaryHeight();

        return {
            left: leftX,
            right: leftX + width,
            top: topY,
            bottom: topY + height,
            width,
            height,
            horizontalCenter: leftX + width / 2,
            verticalCenter: topY + height / 2,
        };
    }

    private getSelfBoundsWithMargin(): Bounds {
        const self = this.getSelfBounds();
        const MARGIN = 10;

        return {
            left: self.left - MARGIN,
            top: self.top - MARGIN,
            right: self.right + MARGIN,
            bottom: self.bottom + MARGIN,
            width: self.width + MARGIN * 2,
            height: self.height + MARGIN * 2,
            horizontalCenter: self.horizontalCenter,
            verticalCenter: self.verticalCenter,
        };
    }

    private lastGuidesKey = '';
    private showGuides(opts: {
        horizontalCenter: boolean;
        verticalCenter: boolean;
        top: boolean;
        bottom: boolean;
        left: boolean;
        right: boolean;
        id: string | null;
    }) {
        const key = `${+opts.horizontalCenter}|${+opts.verticalCenter}|${+opts.top}|
        ${+opts.bottom}|${+opts.left}|${+opts.right}|${opts.id ?? ''}`;

        if (key === this.lastGuidesKey) return;
        this.lastGuidesKey = key;

        const idOrNull = (cond: boolean) => (cond ? opts.id : null);
        this.widgetBoundaryService.widgetHorizontalCenterId.set(idOrNull(opts.horizontalCenter));
        this.widgetBoundaryService.widgetVerticalCenterId.set(idOrNull(opts.verticalCenter));
        this.widgetBoundaryService.widgetTopId.set(idOrNull(opts.top));
        this.widgetBoundaryService.widgetBottomId.set(idOrNull(opts.bottom));
        this.widgetBoundaryService.widgetLeftId.set(idOrNull(opts.left));
        this.widgetBoundaryService.widgetRightId.set(idOrNull(opts.right));
    }

    private lastColumnGuidesKey = '';
    private showColumnGuides(opts: { horizontalCenter: boolean; verticalCenter: boolean }) {
        const key = `${+opts.horizontalCenter}|${+opts.verticalCenter}`;
        if (key === this.lastColumnGuidesKey) return;
        this.lastColumnGuidesKey = key;

        const columnId = this.widget().columnId;
        this.widgetBoundaryService.columnHorizontalCenterId.set(opts.horizontalCenter ? columnId : null);
        this.widgetBoundaryService.columnVerticalCenterId.set(opts.verticalCenter ? columnId : null);
    }

    // --- Drag handlers ---

    public override dragStarted(): void {
        super.dragStarted();
        // Freeze the cumulative-distance baseline before any per-frame write.
        this.dragStartPoint = { x: this.xValue(), y: this.yValue() };
        this.beginGesture();
        this.isDragging.set(true);
        this.overWidgetBoundaryId = null;

        this.collectCandidateTargets();
        this.loadColumnBounds();
    }

    private rAF = 0;
    private pendingEvent: CdkDragMove | null = null;
    public dragMoved(event: CdkDragMove): void {
        this.pendingEvent = event;
        if (this.rAF) return;
        this.rAF = requestAnimationFrame(() => {
            this.rAF = 0;
            const ev = this.pendingEvent!;
            this.pendingEvent = null;
            this.handleDragMove(ev);
        });
    }

    public handleDragMove(event: CdkDragMove): void {
        this.setX(this.getNextXPosition(event.distance.x));
        this.setY(this.getNextYPosition(event.distance.y));

        const { x, y } = event.pointerPosition;
        const hit = this.findHitUnderDragged({ fallback: { cx: x, cy: y } });

        let snapX: number | null = null;
        let snapY: number | null = null;

        if (!hit) {
            this.clearAllGuides();
            this.overWidgetBoundaryId = null;
        } else {
            const centers = this.getBounds(hit);
            this.overWidgetBoundaryId = hit.id;

            const self = this.getSelfBounds();
            const snap = this.computeWidgetSnap(self, centers);

            if (!snap) {
                this.clearAllGuides();
            } else {
                this.showGuides({
                    horizontalCenter: snap.showHorizontal,
                    verticalCenter: snap.showVertical,
                    top: snap.showTop,
                    bottom: snap.showBottom,
                    left: snap.showLeft,
                    right: snap.showRight,
                    id: this.overWidgetBoundaryId,
                });

                if (snap.snapX !== null) {
                    snapX = snap.snapX;
                    this.setX(snapX);
                }
                if (snap.snapY !== null) {
                    snapY = snap.snapY;
                    this.setY(snapY);
                }
            }
        }

        this.applyColumnSnapping(snapX, snapY);
    }

    public dragEndedTest(): void {
        if (this.rAF) {
            cancelAnimationFrame(this.rAF);
            this.rAF = 0;
            this.pendingEvent = null;
        }

        this.dragEnded();
        this.commitGesture();
        this.dragStartPoint = null;

        this.isDragging.set(false);
        this.overWidgetBoundaryId = null;
        this.columnBounds = null;

        this.clearAllGuides();

        this.candidateTargets = [];
    }

    // --- Rotation handlers ---

    public rotationChangeStart(): void {
        this.beginGesture();
        this.isRotating.set(true);
        this.rotateTooltipRef?.show();
    }
    public rotationChange(value: number): void {
        this.setRotate(value);
        this.updateBoundaries();
    }
    public rotationChangeEnd(): void {
        this.commitGesture();
        this.isRotating.set(false);
        this.rotateTooltipRef?.hide();
    }

    // --- Resize handlers ---

    public widthChangeStart(): void {
        this.beginGesture();
    }

    private postWidthChange(): void {
        this.updateBoundaries();
    }

    public widthChangeToRight(value: number): void {
        const delta = value - this.widthValue();
        const isFlipped = Math.cos(this.angle()) < 0;

        if (this.isHorizontalPositionRight() !== isFlipped) {
            this.setX(this.xValue() - delta);
        }

        if (this.keepProportions()) {
            const aspectRatio = this.widthValue() / this.heightValue();
            this.setHeight(value / aspectRatio);
        }

        this.setWidth(value);
        this.postWidthChange();
    }

    public widthChangeToLeft(value: number): void {
        const delta = value - this.widthValue();
        const isFlipped = Math.cos(this.angle()) < 0;

        if (this.isHorizontalPositionLeft() !== isFlipped) {
            this.setX(this.xValue() - delta);
        }

        if (this.keepProportions()) {
            const aspectRatio = this.widthValue() / this.heightValue();
            this.setHeight(value / aspectRatio);
        }

        this.setWidth(value);
        this.postWidthChange();
    }

    public widthChangeEnd(): void {
        this.commitGesture();
    }

    public heightChangeStart(): void {
        this.beginGesture();
    }

    private postHeightChange(): void {
        this.updateBoundaries();
    }

    public heightChangeToBottom(value: number): void {
        const delta = value - this.heightValue();
        const isFlipped = Math.cos(this.angle()) < 0;

        if (this.isVerticalPositionBottom() !== isFlipped) {
            this.setY(this.yValue() - delta);
        }

        if (this.keepProportions()) {
            const aspectRatio = this.widthValue() / this.heightValue();
            this.setWidth(value * aspectRatio);
        }

        this.setHeight(value);
        this.postHeightChange();
    }

    public heightChangeToTop(value: number): void {
        const delta = value - this.heightValue();
        const isFlipped = Math.cos(this.angle()) < 0;

        if (this.isVerticalPositionTop() !== isFlipped) {
            this.setY(this.yValue() - delta);
        }

        if (this.keepProportions()) {
            const aspectRatio = this.widthValue() / this.heightValue();
            this.setWidth(value * aspectRatio);
        }

        this.setHeight(value);
        this.postHeightChange();
    }

    public heightChangeEnd(): void {
        this.commitGesture();
    }

    public sizeChangeStart(): void {
        this.beginGesture();
    }

    private applyCornerResize(value: { width: number; height: number }, left?: boolean, top?: boolean): void {
        const dw = value.width - this.widthValue();
        const dh = value.height - this.heightValue();
        const a = this.angle();

        const localDx = (left ? -1 : 1) * dw;
        const localDy = (top ? -1 : 1) * dh;

        const rawDx = localDx * Math.cos(a) - localDy * Math.sin(a);
        const rawDy = localDx * Math.sin(a) + localDy * Math.cos(a);

        const xSign = this.isHorizontalPositionLeft() ? 1 : -1;
        const ySign = this.isVerticalPositionTop() ? 1 : -1;

        this.setX(this.xValue() + (xSign * rawDx) / 2 - dw / 2);
        this.setY(this.yValue() + (ySign * rawDy) / 2 - dh / 2);

        this.setWidth(value.width);
        this.setHeight(value.height);
        this.updateBoundaries();
    }

    public sizeChangeTopRight(value: { width: number; height: number }): void {
        this.applyCornerResize(value, false, true);
    }
    public sizeChangeTopLeft(value: { width: number; height: number }): void {
        this.applyCornerResize(value, true, true);
    }
    public sizeChangeBottomRight(value: { width: number; height: number }): void {
        this.applyCornerResize(value, false, false);
    }
    public sizeChangeBottomLeft(value: { width: number; height: number }): void {
        this.applyCornerResize(value, true, false);
    }

    public sizeChangeEnd(): void {
        this.commitGesture();
    }

    public dblClickHandler(): void {
        // Entering the rich-text editor: disable direct drag so text selection
        // works (a drag-handle is shown instead — see the template's
        // `@if (!canDrag())` branch).
        if (this.isInlineEditable()) this.canDrag.set(false);
    }
    public blurHandler(): void {
        if (this.isInlineEditable()) this.canDrag.set(true);
        this.hideKeyboardGuidelines();
    }

    public getNextYPosition(distanceY: number): number {
        const startY = this.dragStartPoint?.y ?? this.yValue();
        if (this.isVerticalPositionTop()) return startY + distanceY;
        if (this.isVerticalPositionBottom()) return startY - distanceY;
        return startY;
    }

    public getNextXPosition(distanceX: number): number {
        const startX = this.dragStartPoint?.x ?? this.xValue();
        if (this.isHorizontalPositionLeft()) return startX + distanceX;
        if (this.isHorizontalPositionRight()) return startX - distanceX;
        return startX;
    }

    // --- Derived layout signals (template-bound) ---

    public readonly widgetVisibility = computed<'visible' | 'hidden'>(() => (this.showWidget() ? 'visible' : 'hidden'));
    public readonly widgetOpacity = computed<number>(() => (this.showWidget() ? 1 : 0));
    public readonly widgetTop = computed<string | null>(() => {
        const v = this.topYValue();
        return v !== undefined ? `${v}px` : null;
    });
    public readonly widgetBottom = computed<string | null>(() => {
        const v = this.bottomYValue();
        return v !== undefined ? `${v}px` : null;
    });
    public readonly widgetLeft = computed<string | null>(() => {
        const v = this.leftXValue();
        return v !== undefined ? `${v}px` : null;
    });
    public readonly widgetRight = computed<string | null>(() => {
        const v = this.rightXValue();
        return v !== undefined ? `${v}px` : null;
    });
    public readonly widgetWidth = computed(() => `${this.widgetBoundaryWidth()}px`);
    public readonly widgetHeight = computed(() => `${this.widgetBoundaryHeight()}px`);
    public readonly widgetZIndex = computed(() => (this.isWidgetSelected() ? 100 : this.zIndexValue() + 3));

    public readonly leftXValue = computed(() => (this.isHorizontalPositionLeft() ? this.xValue() : undefined));
    public readonly rightXValue = computed(() => (this.isHorizontalPositionRight() ? this.xValue() : undefined));
    public readonly bottomYValue = computed(() => (this.isVerticalPositionBottom() ? this.yValue() : undefined));
    public readonly topYValue = computed(() => (this.isVerticalPositionTop() ? this.yValue() : undefined));

    // `as ExtendedPositionLegacy`: the form widget's position accessor is union-typed
    // (simple grid on default placement) — this host only ever drives zero forms.
    private readonly extendedPosition = computed(() => this.zeroForm.position().value() as ExtendedPositionLegacy);
    public readonly isVerticalPositionTop = computed(() => this.extendedPosition().positionVertical === 'top');
    public readonly isVerticalPositionBottom = computed(() => this.extendedPosition().positionVertical === 'bottom');
    public readonly isHorizontalPositionLeft = computed(() => this.extendedPosition().positionHorizontal === 'left');
    public readonly isHorizontalPositionRight = computed(() => this.extendedPosition().positionHorizontal === 'right');
    public readonly angle = computed(() => this.rotateValue() * (Math.PI / 180));

    // --- Hide flags (zero-only data) ---

    public readonly hideOnDesktopValue = computed(() => Boolean(this.zeroForm.hideOnDesktop().value()));
    public readonly hideOnTabletValue = computed(() => Boolean(this.zeroForm.hideOnTablet().value()));
    public readonly hideOnMobileValue = computed(() => Boolean(this.zeroForm.hideOnMobile().value()));

    public readonly showWidget = computed(() => {
        if (this.isTablet()) return !this.hideOnTabletValue();
        if (this.isMobile()) return !this.hideOnMobileValue();
        return !this.hideOnDesktopValue();
    });

    public readonly centerX = computed(() => this.widgetBoundaryWidth() / 2);
    public readonly centerY = computed(() => this.widgetBoundaryHeight() / 2);

    // --- Guideline label positions ---

    private readonly MAX_GUIDELINE_POS = 100;
    private readonly MAX_GUIDELINE_GAP = this.MAX_GUIDELINE_POS * 2;

    private clampGuide(v: number | undefined): number {
        const x = v ?? 0;
        if (x < 0) return 0;
        if (x > this.MAX_GUIDELINE_GAP) return this.MAX_GUIDELINE_POS;
        return x / 2;
    }

    public readonly guidelineTopLabelPosition = computed(() => this.clampGuide(this.topYValue()));
    public readonly guidelineLeftLabelPosition = computed(() => this.clampGuide(this.leftXValue()));
    public readonly guidelineRightLabelPosition = computed(() => this.clampGuide(this.rightXValue()));
    public readonly guidelineBottomLabelPosition = computed(() => this.clampGuide(this.bottomYValue()));

    public ngOnDestroy(): void {
        this.hideKeyboardGuidelines();
        this.widgetBoundaryService.removeTargetCandidate(this.widget().id);

        if (this.gestureActive) {
            this.contentFacade.setAutoSavePaused(false);
        }
    }
}
